import type { BadgeTone } from '@/components/ui/badge';

/** One status → (label, tone, form) entry. */
export interface StatusEntry {
  label: string;
  tone: BadgeTone;
  soft: boolean;
}

/**
 * THE status → (label, tone, form) map — the single source of truth for how
 * every status renders (audit C1). One definition; no local copies. The
 * status-color contract lives in `.claude/skills/recovery-engine-design/`.
 *
 * (The invoice-verification bucket vocabulary was removed with the buckets
 * themselves — approval is terminal, so plain finding status is all there is.)
 */
export const STATUS_MAP = {
  finding: {
    approved: { label: 'approved', tone: 'positive', soft: false },
    rejected: { label: 'rejected', tone: 'negative', soft: false },
    needs_review: { label: 'needs review', tone: 'caution', soft: false },
    pending: { label: 'unreviewed', tone: 'info', soft: true },
  },
  // Approval basis — a soft, secondary chip beside the solid status badge on
  // approved findings. `invoice_confirmed` is the audit-ready tier; the other
  // two are estimates.
  approval: {
    invoice_confirmed: { label: 'invoice confirmed', tone: 'positiveStrong', soft: true },
    supplier_sampled: { label: 'supplier sampled', tone: 'info', soft: true },
    practitioner_judgment: { label: 'practitioner judgment', tone: 'muted', soft: true },
  },
  engagement: {
    mnda_pending: { label: 'MNDA pending', tone: 'muted', soft: true },
    intake_pending: { label: 'intake pending', tone: 'muted', soft: true },
    engine_ready: { label: 'engine ready', tone: 'caution', soft: false },
    analyzing: { label: 'analyzing', tone: 'active', soft: false },
    in_review: { label: 'in review', tone: 'active', soft: false },
    evidence_pending: { label: 'evidence pending', tone: 'caution', soft: false },
    publishable: { label: 'publishable', tone: 'positive', soft: false },
    published: { label: 'published', tone: 'positiveStrong', soft: false },
  },
  run: {
    running: { label: 'running', tone: 'active', soft: false },
    in_review: { label: 'in review', tone: 'caution', soft: false },
    completed: { label: 'completed', tone: 'positive', soft: false },
    failed: { label: 'failed', tone: 'negative', soft: false },
  },
  account: {
    in_progress: { label: 'in progress', tone: 'active', soft: false },
    completed: { label: 'completed', tone: 'positive', soft: false },
    reviewing: { label: 'reviewing', tone: 'muted', soft: true },
  },
  // Claim-gap states (claimed-ITC design doc §4.3): claimed reuses the
  // informational blue-gray, partial the existing warning amber, and
  // "not claimed" a neutral outline — none of the approve/reject hues.
  claim: {
    fully_claimed: { label: 'claimed', tone: 'info', soft: false },
    partially_claimed: { label: 'partially claimed', tone: 'caution', soft: false },
    not_claimed: { label: 'not claimed', tone: 'muted', soft: true },
  },
  ruleKind: {
    recovery: { label: 'recovery', tone: 'positive', soft: false },
    compliance_risk: { label: 'compliance risk', tone: 'cautionStrong', soft: false },
  },
} as const satisfies Record<string, Record<string, StatusEntry>>;

export type StatusKind = keyof typeof STATUS_MAP;

/** Look up an entry, or null for an unknown status (caller renders an em-dash). */
export function statusEntry(kind: StatusKind, status: string): StatusEntry | null {
  const group = STATUS_MAP[kind] as Record<string, StatusEntry>;
  return group[status] ?? null;
}
